import React, { useEffect, useState } from 'react';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import { Button, Flex, Popconfirm, Typography, Select, Space } from 'antd';
import Table from 'components/Common/Table';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import useScrollBottom from 'CustomHooks/useScrollBottom';
import dayjs from 'dayjs';
import useUrlFilters from 'hooks/useUrlFilters';

const { Text } = Typography;

// Search and filters live in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  type: { type: 'string' },
  status: { type: 'string' },
};

const CampaignsTable = () => {

  const navigate = useNavigate();
  const bottom = useScrollBottom();

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [dataSource, setDataSource] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [, setSelectedRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const filterType = filters.type || '';
  const setFilterType = urlSetter('type');
  const filterStatus = filters.status || '';
  const setFilterStatus = urlSetter('status');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Search sits outside the filter panel, so it is neither counted nor cleared.
  const activeFilterCount = (filterType ? 1 : 0) + (filterStatus ? 1 : 0);

  const clearFilters = () => {
    setFilters({ type: null, status: null });
    setDataSource([]);
    setPageNumber(0);
  };

  const fetchCampaigns = async (page) => {
    setLoading(true);
    try {
      const response = await actions.loadCampaigns(page, searchTerm, filterType, filterStatus);
      if (response.status == 200) {
        const data = response.data;
        if (page) {
          setDataSource([...dataSource, ...data.results])
          setTotalRows(data.count);
        } else {
          setDataSource([...data.results])
          setTotalRows(data.count);
        }
      }
    } catch (e) {
      console.error('Error fetching campaigns:', e);
    }
    setLoading(false);
  }

  const onSearch = (value) => {
    if (searchTerm === value) {
      // Do nothing if search term hasn't changed
    } else {
      setSearchTerm(value);
      setDataSource([]);
      setPageNumber(0);
    }
  }

  useEffect(() => {
    let p = pageNumber + 1;
    if (p === 1) {
      fetchCampaigns(p);
      setPageNumber(p);
    } else if (dataSource && dataSource.length < totalRows) {
      fetchCampaigns(p);
      setPageNumber(p);
    }
  }, [searchTerm, filterType, filterStatus]);

  useEffect(() => {
    if (bottom && !loading) {
      let p = pageNumber + 1;
      if (dataSource.length < totalRows) {
        fetchCampaigns(p);
        setPageNumber(p);
      }
    }
  }, [bottom]);

  const handleDelete = async (id) => {
    const response = await actions.deleteCampaign(id);
    if (response) {
      // Remove the deleted item from the current dataSource without refetching
      setDataSource(dataSource.filter((item) => item.id !== id));
      setTotalRows(totalRows - 1);
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRecords(selectedRows);
    },
  }

  const defaultColumns = [
    {
      title: 'Campaign Name',
      dataIndex: 'campaign_name',
      key: 'campaign_name',
      sorter: (a, b) => a?.campaign_name.localeCompare(b?.campaign_name),
      render: (text, record) => (
        <a onClick={() => navigate(`/campaigns/edit/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type_display',
      key: 'type_display',
      sorter: (a, b) => a?.type_display.localeCompare(b?.type_display),
    },
    {
      title: 'Status',
      dataIndex: 'status_display',
      key: 'status_display',
      sorter: (a, b) => a?.status_display.localeCompare(b?.status_display),
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      sorter: (a, b) => dayjs(a?.start_date) - dayjs(b?.start_date),
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      sorter: (a, b) => dayjs(a?.end_date) - dayjs(b?.end_date),
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Campaign Owner',
      dataIndex: 'campaign_owner_name',
      key: 'campaign_owner_name',
      sorter: (a, b) => (a?.campaign_owner_name || '').localeCompare(b?.campaign_owner_name || ''),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/campaigns/edit/${record.id}`)}
          >
          </Button>
          <Popconfirm
            title="Delete Campaign"
            description="Are you sure you want to delete this campaign?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Title level={3}>All Campaigns</Typography.Title>
        <Flex gap="middle">
          <Select
            placeholder="All Campaigns"
            style={{ width: 200 }}
            allowClear
          >
            <Select.Option value="">All Campaigns</Select.Option>
          </Select>
        </Flex>
      </Flex>

      <FilterBar
        searchValue={searchTermLive}
        onSearchChange={setSearchTermLive}
        onSearch={onSearch}
        searchPlaceholder="Search campaigns"
        activeCount={activeFilterCount}
        onClear={clearFilters}
        title="Filter Campaigns"
        addLabel="Create Campaign"
        onAdd={() => navigate('/campaigns/create')}
        onRefresh={() => {
              setDataSource([]);
              setPageNumber(0);
              fetchCampaigns(1);
              setPageNumber(1);
            }}
        refreshing={loading}
      >
        <FilterField label="Type">
          <Select
            placeholder="Filter by Type"
            allowClear
            value={filterType || undefined}
            onChange={(value) => {
              setFilterType(value || '');
              setDataSource([]);
              setPageNumber(0);
            }}
          >
            <Select.Option value="CONFERENCE">Conference</Select.Option>
            <Select.Option value="WEBINAR">Webinar</Select.Option>
            <Select.Option value="TRADE_SHOW">Trade Show</Select.Option>
            <Select.Option value="ADVERTISEMENT">Advertisement</Select.Option>
            <Select.Option value="EMAIL">Email</Select.Option>
            <Select.Option value="TELEMARKETING">Telemarketing</Select.Option>
          </Select>
        </FilterField>
        <FilterField label="Status">
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(value) => {
              setFilterStatus(value || '');
              setDataSource([]);
              setPageNumber(0);
            }}
          >
            <Select.Option value="PLANNING">Planning</Select.Option>
            <Select.Option value="ACTIVE">Active</Select.Option>
            <Select.Option value="INACTIVE">Inactive</Select.Option>
            <Select.Option value="COMPLETE">Complete</Select.Option>
          </Select>
        </FilterField>
      </FilterBar>

      <Text type="secondary">Total Records: {totalRows}</Text>

      <Table
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        columns={defaultColumns}
        dataSource={dataSource}
        loading={loading}
        pagination={{
          total: totalRows,
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        rowKey="id"
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default CampaignsTable;
